import React, { useEffect } from "react";
import {
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
  Switch,
  Button,
  Row,
  Col,
  Card,
  Tag,
  Space,
  Divider,
} from "antd";
import {
  CalendarOutlined,
  ClockCircleOutlined,
  EnvironmentOutlined,
  TagOutlined,
  FileTextOutlined,
  StarOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";
import "./EventModal.css";
import api from "../../../services/api"; // Added for delete
import { message } from "antd"; // For messages

const { TextArea } = Input;
const { Option } = Select;

interface EventModalProps {
  visible: boolean;
  event: any;
  categories: any[];
  onCancel: () => void;
  onSave: (eventData: any) => void;
  selectedCompany: { id: string; type: string } | null;
}

const EventModal: React.FC<EventModalProps> = ({
  visible,
  event,
  categories,
  onCancel,
  onSave,
}) => {
  const signature = localStorage.getItem("signature"); // Added
  const [form] = Form.useForm();

  // Handle all day switch change
  const handleAllDayChange = (allDay: boolean) => {
    const start = form.getFieldValue("start");
    const end = form.getFieldValue("end");

    if (allDay) {
      if (start) form.setFieldValue("start", start.startOf("day"));
      if (end) form.setFieldValue("end", end.endOf("day"));
    } else {
      const now = dayjs();
      if (start)
        form.setFieldValue(
          "start",
          start.startOf("day").set("hour", 9).set("minute", 0)
        );
      if (end)
        form.setFieldValue(
          "end",
          end.startOf("day").set("hour", 17).set("minute", 0)
        );
    }
  };

  const handleStartChange = (start: Dayjs) => {
    const end = form.getFieldValue("end");
    const allDay = form.getFieldValue("allDay");

    if (start && end && start.isAfter(end)) {
      if (allDay) {
        form.setFieldValue("end", start.endOf("day"));
      } else {
        form.setFieldValue("end", start.add(1, "hour"));
      }
    }
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      let { start, end, allDay } = values;

      if (allDay) {
        start = start.startOf("day");
        end = end.endOf("day");
      }

      onSave({
        ...values,
        start,
        end,
        id: event?.id,
      });
      form.resetFields();
      document.getElementById("cancel-btn-event-modal")?.click();
    });
  };

  const handleCancel = () => {
    form.resetFields();
    onCancel();
  };

  const handleDelete = async () => {
    if (!event?.id) return;
    try {
      await api.post("activityCalendarService/deleteEvent", {
        signature,
        id: event.id,
      });
      message.success("Event deleted successfully");
      onCancel();
    } catch (err) {
      message.error("Failed to delete event");
    }
  };

  useEffect(() => {
    if (visible) {
      if (event) {
        form.setFieldsValue({
          title: event.title || "",
          description: event.description || "",
          start: event.start || null,
          end: event.end || null,
          allDay: event.allDay || false,
          category: event.category || categories[0]?.name,
          color: event.color || categories[0]?.color,
          is_public: event.is_public || false, // Added
        });
      } else {
        const now = dayjs();
        const defaultStart = now.set("hour", 9).set("minute", 0);
        const defaultEnd = now.set("hour", 17).set("minute", 0);

        form.setFieldsValue({
          title: "",
          description: "",
          start: defaultStart,
          end: defaultEnd,
          allDay: false,
          category: categories[0]?.name,
          color: categories[0]?.color,
          is_public: false, // Added
        });
      }
    }
  }, [visible, event, form, categories]);

  const isAllDay = Form.useWatch("allDay", form);
  const selectedCategory = Form.useWatch("category", form);
  const selectedCategoryColor = categories.find(
    (cat) => cat.name === selectedCategory
  )?.color;

  return (
    <Modal
      title={
        <div className="modal-header">
          <CalendarOutlined className="modal-header-icon" />
          <span>{event ? "Edit Event" : "Create New Event"}</span>
          {selectedCategoryColor && (
            <div
              className="category-indicator"
              style={{ backgroundColor: selectedCategoryColor }}
            />
          )}
        </div>
      }
      open={visible}
      onCancel={handleCancel}
      footer={[
        event && ( // Added delete button if editing
          <Button
            key="delete"
            danger
            onClick={handleDelete}
            size="large"
            className="delete-btn"
          >
            Delete
          </Button>
        ),
        <Button
          key="cancel"
          onClick={handleCancel}
          size="large"
          className="cancel-btn"
          id="cancel-btn-event-modal"
        >
          Cancel
        </Button>,
        <Button
          key="save"
          type="primary"
          onClick={handleSave}
          size="large"
          className="save-btn"
          icon={<StarOutlined />}
        >
          {event ? "Update Event" : "Create Event"}
        </Button>,
      ]}
      width={700}
      className="event-modal"
      styles={{
        body: { padding: "24px 0" },
        header: { borderBottom: "1px solid #f0f0f0", marginBottom: 0 },
      }}
    >
      <div className="modal-content">
        <Form form={form} layout="vertical" className="event-form">
          {/* Event Title Section */}
          <Card
            size="small"
            className="form-section-card"
            styles={{ body: { padding: "16px 20px" } }}
          >
            <Form.Item
              name="title"
              label={
                <span className="form-label">
                  <FileTextOutlined /> Event Title
                </span>
              }
              rules={[{ required: true, message: "Please enter event title" }]}
            >
              <Input
                placeholder="What's the event about?"
                size="large"
                className="title-input"
                variant="borderless"
              />
            </Form.Item>
          </Card>

          <Space
            direction="vertical"
            size="middle"
            style={{ width: "100%", marginTop: 16 }}
          >
            {/* Description Section */}
            <Card
              size="small"
              className="form-section-card"
              styles={{ body: { padding: "16px 20px" } }}
            >
              <Form.Item
                name="description"
                label={
                  <span className="form-label">
                    <FileTextOutlined /> Description
                  </span>
                }
              >
                <TextArea
                  rows={3}
                  placeholder="Add details about your event..."
                  className="description-textarea"
                  variant="borderless"
                />
              </Form.Item>
            </Card>

            {/* Date & Time Section */}
            <Card
              size="small"
              className="form-section-card"
              styles={{ body: { padding: "16px 20px" } }}
            >
              <div className="section-header">
                <ClockCircleOutlined className="section-icon" />
                <span className="section-title">Date & Time</span>
              </div>

              <Form.Item
                name="allDay"
                valuePropName="checked"
                className="all-day-item"
              >
                <Switch
                  onChange={handleAllDayChange}
                  checkedChildren="All Day"
                  unCheckedChildren="Specific Time"
                  className="all-day-switch"
                />
              </Form.Item>

              <Row gutter={16} className="datetime-row">
                <Col span={12}>
                  <Form.Item
                    name="start"
                    label="Start"
                    rules={[
                      { required: true, message: "Please select start time" },
                    ]}
                    className="datetime-item"
                  >
                    <DatePicker
                      showTime={
                        !isAllDay
                          ? {
                              format: "h:mm A",
                              use12Hours: true,
                              hourStep: 1,
                              minuteStep: 15,
                              showNow: false,
                            }
                          : false
                      }
                      format={
                        isAllDay
                          ? "ddd, MMM D, YYYY"
                          : "ddd, MMM D, YYYY h:mm A"
                      }
                      style={{ width: "100%" }}
                      size="large"
                      onChange={handleStartChange}
                      disabled={isAllDay}
                      disabledTime={isAllDay ? () => ({}) : undefined}
                      suffixIcon={<CalendarOutlined />}
                      className="datetime-picker"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="end"
                    label="End"
                    rules={[
                      { required: true, message: "Please select end time" },
                    ]}
                    className="datetime-item"
                  >
                    <DatePicker
                      showTime={
                        !isAllDay
                          ? {
                              format: "h:mm A",
                              use12Hours: true,
                              hourStep: 1,
                              minuteStep: 15,
                              showNow: false,
                            }
                          : false
                      }
                      format={
                        isAllDay
                          ? "ddd, MMM D, YYYY"
                          : "ddd, MMM D, YYYY h:mm A"
                      }
                      style={{ width: "100%" }}
                      size="large"
                      disabled={isAllDay}
                      disabledTime={isAllDay ? () => ({}) : undefined}
                      suffixIcon={<CalendarOutlined />}
                      className="datetime-picker"
                    />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            {/* Category & Public Section */}
            <Row gutter={16}>
              <Col span={12}>
                <Card
                  size="small"
                  className="form-section-card"
                  styles={{ body: { padding: "16px 20px" } }}
                >
                  <div className="section-header">
                    <TagOutlined className="section-icon" />
                    <span className="section-title">Category</span>
                  </div>
                  <Form.Item name="category" className="category-item">
                    <Select
                      size="large"
                      placeholder="Select category"
                      className="category-select"
                    >
                      {categories
                        .filter(
                          (cat) =>
                            cat.name !== "Birthday" &&
                            cat.name !== "Anniversary" &&
                            cat.name !== "Holiday"
                        )
                        .map((cat) => (
                          <Option key={cat.id} value={cat.name}>
                            <Space>
                              <div
                                className="category-color-dot"
                                style={{ backgroundColor: cat.color }}
                              />
                              {cat.name}
                            </Space>
                          </Option>
                        ))}
                    </Select>
                  </Form.Item>
                </Card>
              </Col>
              <Col span={12}>
                <Card
                  size="small"
                  className="form-section-card"
                  styles={{ body: { padding: "16px 20px" } }}
                >
                  <div className="section-header">
                    <EnvironmentOutlined className="section-icon" />
                    <span className="section-title">Visibility</span>
                  </div>
                  <Form.Item
                    name="is_public"
                    valuePropName="checked"
                    className="location-item"
                  >
                    <Switch
                      checkedChildren="Public"
                      unCheckedChildren="Private"
                    />
                  </Form.Item>
                </Card>
              </Col>
            </Row>
          </Space>

          <Form.Item name="color" hidden>
            <Input />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default EventModal;
